import { useCallback, useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { mdiDeleteOutline } from '@mdi/js'
import { useIntl } from 'react-intl'

import {
  mistOrganizationIdSelector,
  mistSiteIdSelector,
} from 'features/networkAi/selectors'
import { deleteClaimDeviceThunk } from 'features/networkAi/slice'
import { Inventory } from 'features/networkAi/types'
import { getSortOptions } from 'components/Table/utils'
import { Table, MobileSortBy } from 'components/Table'
import { makePublicUrl } from 'app/utils/publicUrl'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { DASH_SYMBOL } from 'app/consts'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { deviceListMessages } from '../messages'
import { commonMessages } from 'app/i18n/messages'

import MobileDeviceListComponent from './MobileDeviceListComponent'
import {
  ClaimDeviceTableImage,
  ClaimDeviceTableTitle,
  DeleteClaimDeviceButton,
  DeleteClaimDeviceButtonIcon,
} from './styles'

interface DeviceListTableProps {
  devices: Inventory[]
}

function DeviceListTable({ devices }: DeviceListTableProps) {
  const dispatch = useAppDispatch()
  const organizationId = useAppSelector(mistOrganizationIdSelector)
  const siteId = useAppSelector(mistSiteIdSelector)

  const { formatMessage } = useIntl()
  const [deleteDeviceMac, setDeleteDeviceMac] = useState('')
  //const [deleteDeviceMagic,setDeleteDeviceMagic] = useState('')
  const [isDeleteAPModalOpen, setIsDeleteAPModalOpen] = useState(false)

  const handleClickToDeleteClaimDevice = useCallback(
    (mac: string) => {
      setDeleteDeviceMac(mac)
      //setDeleteDeviceMagic(magic)
      setIsDeleteAPModalOpen(true)
    },
    [setDeleteDeviceMac, setIsDeleteAPModalOpen]
  )

  const handleConfirmToDeleteClaimDevice = () => {
    setIsDeleteAPModalOpen(false)
    handleConfirmDeleteClaimDevice(deleteDeviceMac)
  }

  const handleCancelToDeleteClaimDevice = () => {
    setIsDeleteAPModalOpen(false)
    setDeleteDeviceMac('')
    //setDeleteDeviceMagic('')
  }

  const handleConfirmDeleteClaimDevice = useCallback(
    (mac: string) => {
      dispatch(deleteClaimDeviceThunk(organizationId, siteId, mac))
    },
    [dispatch, organizationId, siteId]
  )

  const headers = useMemo(
    () => ({
      name: {
        id: 'name',
        label: 'Name',
      },
      mac: {
        id: 'mac',
        label: 'MAC Address',
      },
      model: {
        id: 'model',
        label: 'Model',
      },
    }),
    []
  )

  const columns: readonly Column<Inventory>[] = useMemo(
    () => [
      {
        id: 'icon',
        width: '0',
        flex: 1,
        Cell: () => {
          return (
            <ClaimDeviceTableImage
              src={makePublicUrl(`images/networkIcons/access-points.svg`)}
            />
          )
        },
      },
      {
        Header: headers.name.label,
        id: headers.name.id,
        accessor: 'name',
        Cell: (cell: Cell<Inventory>) => {
          return <ClaimDeviceTableTitle>{cell.value}</ClaimDeviceTableTitle>
        },
      },
      {
        Header: headers.mac.label,
        id: headers.mac.id,
        accessor: 'mac',
        Cell: (cell: Cell<Inventory>) => {
          return cell.value ? cell.value.match(/.{2}/g).join(':') : DASH_SYMBOL
        },
      },
      {
        Header: headers.model.label,
        id: headers.model.id,
        accessor: 'model',
        Cell: (cell: Cell<Inventory>) => {
          return cell.value ? cell.value.toUpperCase() : DASH_SYMBOL
        },
      },
      {
        id: 'delete',
        width: '20',
        Cell: (cell: Cell<Inventory>) => {
          return (
            <DeleteClaimDeviceButton
              onClick={() =>
                handleClickToDeleteClaimDevice(cell.row.original.mac)
              }
            >
              <DeleteClaimDeviceButtonIcon path={mdiDeleteOutline} size={1} />
            </DeleteClaimDeviceButton>
          )
        },
      },
    ],
    [handleClickToDeleteClaimDevice, headers]
  )

  const sortOptions = useMemo(() => getSortOptions(headers, true), [headers])
  const defaultSort = sortOptions[0]
  const [sortBy, setSortBy] = useState<SortingRule<Inventory>>(defaultSort)

  return (
    <>
      <ConfirmModal
        isOpen={isDeleteAPModalOpen}
        title={formatMessage(deviceListMessages.title)}
        confirmBtnTitle={formatMessage(deviceListMessages.confirmMessage)}
        discardBtnTitle={formatMessage(commonMessages.cancel)}
        variant="primary"
        onConfirm={handleConfirmToDeleteClaimDevice}
        onDiscard={handleCancelToDeleteClaimDevice}
      />
      <MobileSortBy<Inventory>
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      <Table
        columns={columns}
        defaultSortBy={defaultSort}
        data={devices}
        setSortBy={setSortBy}
        sortBy={sortBy}
        mobileViewComponent={MobileDeviceListComponent}
      />
    </>
  )
}

export default DeviceListTable
